<template>
  <transition name="modal">
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm"
      style="
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
      "
      @click.self="closeModal">
      <div
        :class="modalClass"
        class="relative z-[9999] w-72 rounded-lg p-6 text-center shadow-lg md:w-[367px]">
        <h2
          v-if="title"
          class="font-secondary min-h-7 text-xl !font-bold text-dark"
          :class="{ '!text-brand': applyThemeColor }">
          {{ title }}
        </h2>
        <component
          v-if="showBell"
          :is="notificationIcon"
          class="absolute right-4 top-6 h-6 w-6"
          :class="{ '!top-4': !title }" />

        <p
          class="font-primary my-4 text-dark"
          :class="{ '!text-brand': applyThemeColor }"
          v-html="description"></p>

        <div v-if="icon" class="flex w-full justify-center">
          <IconButton
            class="flex items-center justify-center rounded-gc-lg !border-dark !bg-dark px-8 py-1.5"
            :iconClass="'h-5 w-5'"
            ariaLabel="close"
            @click="closeModal"
            :icon="icon" />
        </div>
        <button
          v-else
          @click="closeModal"
          class="w-[100px] rounded-lg bg-dark px-4 py-2"
          :class="[buttonTextCustomClass, buttonTextClass]">
          {{ buttonText }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, defineAsyncComponent, onUnmounted, watch, ref } from 'vue';
import IconButton from '@/components/customComponents/IconButton.vue';
const notificationIcon = defineAsyncComponent(
  () => import('@icons/notification.svg'),
);

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: 'Description',
  },
  buttonText: {
    type: String,
    default: 'Close',
  },
  type: {
    type: String,
    default: 'success',
    validator: (value) =>
      ['success', 'warning', 'danger', 'shoppingRecovery', 'neutral'].includes(
        value,
      ),
  },
  icon: {
    type: Object,
    required: false,
  },
  showBell: {
    type: Boolean,
    default: false,
  },
  buttonTextCustomClass: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:isVisible']);

// Prevent accidental clicks on mobile for first 300ms
const isClickEnabled = ref(false);

// Watch for modal visibility to enable clicks after delay
watch(
  () => props.isVisible,
  (isVisible) => {
    if (isVisible) {
      isClickEnabled.value = false;
      setTimeout(() => {
        isClickEnabled.value = true;
      }, 300);
    }
  },
);

const modalClass = computed(() => {
  return {
    'bg-success': props.type === 'success',
    'bg-warning': props.type === 'warning',
    '!bg-[#f1f1f1]': props.type === 'neutral',
    'bg-error': props.type === 'danger',
    'bg-bg-main': props.type === 'shoppingRecovery',
  };
});

const buttonTextClass = computed(() => {
  return {
    'text-success': props.type === 'success',
    'text-warning': props.type === 'warning',
    'text-white': props.type === 'neutral',
    'text-error': props.type === 'danger',
    'text-light': props.type === 'shoppingRecovery',
  };
});

const applyThemeColor = computed(() => {
  return props.type === 'shoppingRecovery' ? true : false;
});

const closeModal = () => {
  if (!isClickEnabled.value) {
    return;
  }
  emit('update:isVisible', false);
};

// Handle Esc key to close modal - optimized implementation
const handleKeyDown = (event) => {
  if (event.key === 'Escape') {
    closeModal();
  }
};

// Only add/remove listener when modal is visible
watch(
  () => props.isVisible,
  (isVisible) => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }
  },
  { immediate: true },
);

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown);
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.5s;
}
.modal-enter,
.modal-leave-to {
  @apply opacity-0;
}
.modal-enter-from {
  @apply opacity-0;
}
.modal-enter-to {
  @apply opacity-100;
}

/* Ensure modal is always centered and stable */
.fixed.inset-0 {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  transform: none !important;
}
</style>
